import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import swrConfig from 'utils/swrConfig';
import type { SelectMultiChoicelistOption } from './SelectMultiChoicelist';

export type OptionValue = string | number;

export interface OptionsPage<T extends OptionValue> {
    options: SelectMultiChoicelistOption<T>[];
    nextCursor?: string | null;
}

interface UsePageableOptionsParams<T extends OptionValue> {
    key: (string | number)[];
    fetchPage(cursor: string | null): Promise<OptionsPage<T>>;
    fetchByValues(values: T[]): Promise<SelectMultiChoicelistOption<T>[]>;
    values: T[];
    enabled?: boolean;
}

export interface PageableOptions<T extends OptionValue> {
    options: SelectMultiChoicelistOption<T>[];
    selectedOptions: SelectMultiChoicelistOption<T>[];
    isResolvingSelection: boolean;
    hasMore: boolean;
    isLoadingMore: boolean;
    hasPageError: boolean;
    unresolvedValues: T[];
    loadMore(): void;
    retry(): void;
}

const FIRST_PAGE_CURSOR = '';

export const usePageableOptions = <T extends OptionValue>({
    key,
    fetchPage,
    fetchByValues,
    values,
    enabled = true,
}: UsePageableOptionsParams<T>): PageableOptions<T> => {
    const labelCache = useRef(new Map<T, string>());
    const [cacheVersion, setCacheVersion] = useState(0);

    const remember = useCallback((options: SelectMultiChoicelistOption<T>[]) => {
        const added = options.filter((option) => labelCache.current.get(option.value) !== option.label);
        added.forEach((option) => labelCache.current.set(option.value, option.label));

        if (added.length) setCacheVersion((version) => version + 1);

        return options;
    }, []);

    const {
        data: pages,
        error: pageError,
        isLoading: isLoadingFirstPage,
        size,
        setSize,
        mutate: revalidatePages,
    } = useSWRInfinite<OptionsPage<T>>(
        (index, previousPage) => {
            if (!enabled) return null;
            if (index === 0) return [...key, 'page', FIRST_PAGE_CURSOR];

            const cursor = previousPage?.nextCursor;
            return cursor ? [...key, 'page', cursor] : null;
        },
        (swrKey) => {
            const parts = swrKey as (string | number)[];
            const cursor = String(parts[parts.length - 1]);

            return fetchPage(cursor === FIRST_PAGE_CURSOR ? null : cursor).then((page) => ({
                ...page,
                options: remember(page.options),
            }));
        },
        { ...swrConfig, revalidateFirstPage: false }
    );

    const options = useMemo(() => {
        const seen = new Set<T>();

        return (pages ?? [])
            .flatMap((page) => page.options)
            .filter((option) => {
                if (seen.has(option.value)) return false;
                seen.add(option.value);
                return true;
            });
    }, [pages]);

    // labelCache is per-mount (a fresh, empty ref every time this hook mounts — e.g. navigating away
    // from and back to this row), but SWR's cache is global and, with revalidateIfStale: false, hands
    // back already-known `pages`/`resolved` data on a fresh mount WITHOUT re-invoking the fetcher — so
    // `remember` (normally only run from inside the fetcher below) would never re-run and labelCache
    // would stay empty forever. These effects re-seed it from whatever SWR already has, fetcher or not.
    useEffect(() => {
        if (pages) remember(pages.flatMap((page) => page.options));
    }, [pages, remember]);

    const missing = useMemo(() => values.filter((value) => !labelCache.current.has(value)), [values, cacheVersion]);

    const {
        data: resolved,
        error: resolveError,
        isValidating: isResolving,
    } = useSWR(
        missing.length ? [...key, 'by-values', ...[...missing].sort()] : null,
        () => fetchByValues(missing).then(remember),
        { ...swrConfig, keepPreviousData: true }
    );

    useEffect(() => {
        if (resolved) remember(resolved);
    }, [resolved, remember]);

    const selectedOptions = useMemo(
        () =>
            values
                .filter((value) => labelCache.current.has(value))
                .map((value) => ({ value, label: labelCache.current.get(value) as string })),
        [values, labelCache, cacheVersion]
    );

    const isResolvingSelection = Boolean(missing.length) && (isResolving || (resolved === undefined && !resolveError));

    const lastPage = pages?.[pages.length - 1];
    const hasMore = Boolean(lastPage?.nextCursor);
    const isLoadingMore = isLoadingFirstPage || (size > 0 && !!pages && pages[size - 1] === undefined);

    const requestedSize = useRef(0);

    const loadMore = useCallback(() => {
        const next = size + 1;
        if (requestedSize.current >= next) return;

        requestedSize.current = next;
        void setSize(next);
    }, [size, setSize]);

    const retry = useCallback(() => {
        void revalidatePages();
    }, [revalidatePages]);

    return {
        options,
        selectedOptions,
        isResolvingSelection,
        hasMore,
        isLoadingMore,
        hasPageError: Boolean(pageError),
        unresolvedValues: isResolvingSelection || resolveError ? [] : missing,
        loadMore,
        retry,
    };
};
